import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { AdminTitle, Btn, Field, inputCls } from "@/pages/admin/Admin";
import { Uploader } from "@/pages/admin/Products";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const BLANK = { kind: "blog", title: "", slug: "", excerpt: "", content: "", cover: "", video_url: "", seo_title: "", seo_description: "", published: true };

function PostForm({ initial, onSaved }) {
  const [f, setF] = useState(initial);
  const s = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    try { f.post_id ? await api.put(`/admin/posts/${f.post_id}`, f) : await api.post("/admin/posts", f); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4 text-sm" data-testid="post-form">
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Type"><select value={f.kind} onChange={s("kind")} className={inputCls} data-testid="post-kind-select"><option value="blog">Blog</option><option value="vlog">Vlog</option></select></Field>
        <Field label="Title" className="sm:col-span-2"><input value={f.title} onChange={s("title")} className={inputCls} data-testid="post-title-input" /></Field>
      </div>
      <Field label="Excerpt"><input value={f.excerpt} onChange={s("excerpt")} className={inputCls} data-testid="post-excerpt-input" /></Field>
      <Field label="Cover image URL"><div className="flex gap-2"><input value={f.cover} onChange={s("cover")} className={inputCls} data-testid="post-cover-input" /><Uploader accept="image/*" onDone={(d) => setF({ ...f, cover: d.url })} testid="post-cover-upload" /></div></Field>
      {f.cover && <img src={fileUrl(f.cover)} alt="" className="h-24 object-cover" />}
      <Field label="Video (YouTube link or uploaded MP4)"><div className="flex gap-2"><input value={f.video_url} onChange={s("video_url")} className={inputCls} data-testid="post-video-input" /><Uploader accept="video/*" onDone={(d) => setF({ ...f, video_url: d.url })} label="Upload video" testid="post-video-upload" /></div></Field>
      <Field label="Content (blank line = new paragraph)"><textarea rows={8} value={f.content} onChange={s("content")} className={inputCls} data-testid="post-content-input" /></Field>
      <div className="border-t border-[#E6DFD5] pt-4 grid sm:grid-cols-2 gap-4">
        <Field label="SEO title"><input value={f.seo_title} onChange={s("seo_title")} className={inputCls} data-testid="post-seo-title" /></Field>
        <Field label="URL slug"><input value={f.slug} onChange={s("slug")} placeholder="auto" className={inputCls} /></Field>
        <Field label="SEO / OpenGraph description" className="sm:col-span-2"><textarea rows={2} value={f.seo_description} onChange={s("seo_description")} className={inputCls} data-testid="post-seo-description" /></Field>
      </div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2"><Switch checked={f.published} onCheckedChange={s("published")} data-testid="post-published-switch" />Published</label>
        <Btn onClick={save} data-testid="post-save-btn">Save</Btn>
      </div>
    </div>
  );
}

export default function Posts() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/posts").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const del = async (p) => { if (!window.confirm(`Delete "${p.title}"?`)) return; await api.delete(`/admin/posts/${p.post_id}`); load(); };
  return (
    <div data-testid="admin-posts">
      <AdminTitle action={<Btn onClick={() => setEdit(BLANK)} className="flex items-center gap-2" data-testid="new-post-btn"><Plus size={16} />New post</Btn>}>Blog & Vlog</AdminTitle>
      <p className="text-xs text-[#6E6259] mb-4">Sitemap for search engines: <span className="font-mono">{process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml</span></p>
      <div className="bg-white border border-[#E6DFD5] divide-y divide-[#E6DFD5]">
        {rows.map((p) => (
          <div key={p.post_id} className="p-4 flex items-center gap-4 text-sm" data-testid={`admin-post-row-${p.slug}`}>
            <img src={fileUrl(p.cover)} alt="" className="w-16 h-10 object-cover" />
            <span className="font-mono text-[10px] uppercase text-[#8C4F27] w-10">{p.kind}</span>
            <span className="flex-1">{p.title}</span>
            <span className="text-xs text-[#6E6259]">{p.published ? "Published" : "Draft"}</span>
            <button onClick={() => setEdit(p)} aria-label="Edit" data-testid={`edit-post-${p.slug}`}><Pencil size={14} /></button>
            <button onClick={() => del(p)} aria-label="Delete" data-testid={`delete-post-${p.slug}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-serif text-3xl font-normal">{edit?.post_id ? "Edit post" : "New post"}</DialogTitle>
          {edit && <PostForm initial={edit} onSaved={() => { setEdit(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
